/**
 * The keyboard layer (`110-design-system.md` §7 — "a single `mod` abstraction… do not pull in
 * a heavy library for this"). `mod` is Cmd on macOS and Ctrl elsewhere.
 *
 * M7 completes it: bare-key bindings, leader sequences (`G` then `c`), and the one rule that
 * makes both safe — **§7: "shortcuts never fire while a text input has focus, except the editor
 * bindings and the explicitly global set (`Mod+K/S/E/N/\`). Leader sequences never fire in a
 * text field."**
 *
 * That rule is encoded, not remembered: a hotkey with `mod: true` fires everywhere (it cannot
 * collide with typing), and a hotkey *without* it is suppressed whenever the user is typing or
 * an overlay is layered over the app. There is no third case and no per-call-site opt-in.
 *
 * ## One dispatcher, not one listener per hook
 *
 * There is exactly one user with exactly one keyboard, and the leader state (`G` is armed) is a
 * property of *that*, not of any component. With a `window` listener per `useHotkeys()` call,
 * the shell's listener would arm `G` and the tree's listener — receiving the very same event a
 * microsecond later, and knowing nothing about leaders — would disarm it again. So every hook
 * registers into one module-level table, and one listener resolves the whole keymap. First
 * registration wins a collision, which also means a binding can no longer be shadowed by
 * accident from two files at once.
 *
 * The single source of truth for *what* is bound is `keyboard/keymap.ts`, and the `?` cheatsheet
 * is generated from it — so the sheet cannot drift from the bindings, which is the whole of
 * roadmap M7 criterion 3.
 */
import { useEffect } from 'react';

export interface Hotkey {
  /** Lower-case `event.key`: `"\\"`, `"."`, `"k"`, `"?"`, `"backspace"`, `"enter"`. */
  key: string;
  mod?: boolean;
  shift?: boolean;
  alt?: boolean;
  /**
   * A two-key sequence: press `leader`, then `key` within `LEADER_MS`. `G` then `c` → calendar.
   * Leader steps are bare keys and therefore never fire in a text field.
   */
  leader?: string;
  handler: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform);

/** How long a leader stays armed. Long enough to be deliberate, short enough to forget. */
const LEADER_MS = 1_200;

function modPressed(event: KeyboardEvent): boolean {
  return isMac ? event.metaKey : event.ctrlKey;
}

/**
 * A keystroke belongs to the app's chrome only when the user is not typing into something, and
 * only when nothing is layered over it — `?` must not open the cheatsheet while the palette's
 * input contains that letter, and `g` must not arm a leader inside the Tiptap document.
 *
 * The palette and every dialog are Radix overlays, so a live `[role="dialog"]` in the document
 * is the one test that covers all of them and needs no coupling to any of their stores. Add
 * nothing here when a new overlay arrives; it is already covered.
 */
export function isTypingOrOverlaid(target: EventTarget | null): boolean {
  if (typeof document !== 'undefined' && document.querySelector('[role="dialog"]') !== null) {
    return true;
  }
  if (!(target instanceof HTMLElement)) return false;

  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    target.closest('[role="dialog"]') !== null
  );
}

/* ── The dispatcher ─────────────────────────────────────────────────────────── */

/** Insertion-ordered: a `Set` of the arrays each mounted hook registered. */
const registered = new Set<readonly Hotkey[]>();

let armed: { key: string; at: number } | null = null;
let listening = false;

function matches(hotkey: Hotkey, event: KeyboardEvent, key: string): boolean {
  if (key !== hotkey.key.toLowerCase()) return false;
  if ((hotkey.mod ?? false) !== modPressed(event)) return false;
  if ((hotkey.shift ?? false) !== event.shiftKey) return false;
  if ((hotkey.alt ?? false) !== event.altKey) return false;
  return true;
}

function onKeyDown(event: KeyboardEvent): void {
  const key = event.key.toLowerCase();
  const typing = isTypingOrOverlaid(event.target);
  const leaderArmed = armed !== null && Date.now() - armed.at < LEADER_MS ? armed.key : null;

  const all = [...registered].flat();

  for (const hotkey of all) {
    if (!matches(hotkey, event, key)) continue;

    // §7's rule, encoded. `Mod`-modified bindings are the "explicitly global set" and fire from
    // anywhere, including inside the editor. Everything else waits its turn.
    if (hotkey.mod !== true && typing) continue;

    // WHILE A LEADER IS ARMED, ONLY ITS SEQUENCES MAY FIRE.
    //
    // Without this, `G` then `C` would race the calendar's bare `C` (new event) for the same
    // keystroke and the winner would be whichever component happened to mount first — a bug
    // that only shows up on the calendar, only for two of the five `G` sequences, and only
    // sometimes. A leader press is a statement of intent: the next key belongs to it.
    if (leaderArmed !== null && hotkey.leader !== leaderArmed) continue;
    if (leaderArmed === null && hotkey.leader !== undefined) continue;

    armed = null;
    event.preventDefault();
    hotkey.handler();
    return;
  }

  // A key that completed no sequence still consumes the armed leader. `G` then `x` is not a
  // shortcut, and it must not silently become one later.
  if (leaderArmed !== null) {
    armed = null;
    return;
  }

  // Nothing claimed it. A bare, unmodified key that something is waiting on becomes the leader.
  if (typing || event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return;

  if (all.some((hotkey) => hotkey.leader === key)) {
    armed = { key, at: Date.now() };
    return;
  }

  // Any other unclaimed key disarms. `G` then `x` is not a sequence, and leaving `G` armed would
  // make a later, innocent `c` teleport the user to the calendar.
  armed = null;
}

export function useHotkeys(hotkeys: readonly Hotkey[]): void {
  useEffect(() => {
    registered.add(hotkeys);

    if (!listening) {
      listening = true;
      window.addEventListener('keydown', onKeyDown);
    }

    return () => {
      registered.delete(hotkeys);
      if (registered.size === 0) {
        listening = false;
        window.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [hotkeys]);
}
